import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@/routes';
import { SHOW_PRICING } from '@/lib/flags';
import { MarketingLayout } from '@/components/marketing/MarketingLayout';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { AccountLayout } from '@/pages/account/AccountLayout';

const Home = lazy(() => import('@/pages/home/HomePage'));
const Features = lazy(() => import('@/pages/features/FeaturesPage'));
const HowItWorks = lazy(() => import('@/pages/how-it-works/HowItWorksPage'));
const Pricing = lazy(() => import('@/pages/pricing/PricingPage'));
const Download = lazy(() => import('@/pages/download/DownloadPage'));
const Changelog = lazy(() => import('@/pages/changelog/ChangelogPage'));
const Faq = lazy(() => import('@/pages/faq/FaqPage'));
const Privacy = lazy(() => import('@/pages/legal/PrivacyPage'));
const Terms = lazy(() => import('@/pages/legal/TermsPage'));
const Dpa = lazy(() => import('@/pages/legal/DpaPage'));
const NotFound = lazy(() => import('@/pages/NotFoundPage'));

const SignIn = lazy(() => import('@/pages/auth/SignInPage'));
const SignUp = lazy(() => import('@/pages/auth/SignUpPage'));
const Callback = lazy(() => import('@/pages/auth/CallbackPage'));
const Forgot = lazy(() => import('@/pages/auth/ForgotPage'));
const Reset = lazy(() => import('@/pages/auth/ResetPage'));
const CheckEmail = lazy(() => import('@/pages/auth/CheckEmailPage'));

const Account = lazy(() => import('@/pages/account/AccountPage'));
const AccountStats = lazy(() => import('@/pages/account/stats/StatsPage'));
const AccountDevices = lazy(() => import('@/pages/account/devices/DevicesPage'));
const AccountPreferences = lazy(() => import('@/pages/account/preferences/PreferencesPage'));

function RouteFallback() {
  const { t } = useTranslation('common');
  return (
    <div
      className="flex h-screen items-center justify-center text-muted-foreground"
      role="status"
      aria-label={t('actions.loading')}
    />
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RootLayout() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <ScrollToTop />
      <Outlet />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        element: <MarketingLayout />,
        children: [
          { path: ROUTES.home, element: <Home /> },
          { path: ROUTES.features, element: <Features /> },
          { path: ROUTES.howItWorks, element: <HowItWorks /> },
          ...(SHOW_PRICING ? [{ path: ROUTES.pricing, element: <Pricing /> }] : []),
          { path: ROUTES.download, element: <Download /> },
          { path: ROUTES.changelog, element: <Changelog /> },
          { path: ROUTES.faq, element: <Faq /> },
          { path: ROUTES.privacy, element: <Privacy /> },
          { path: ROUTES.terms, element: <Terms /> },
          { path: ROUTES.legalDpa, element: <Dpa /> },
        ],
      },
      {
        element: <AuthLayout />,
        children: [
          { path: ROUTES.authSignIn, element: <SignIn /> },
          { path: ROUTES.authSignUp, element: <SignUp /> },
          { path: ROUTES.authCallback, element: <Callback /> },
          { path: ROUTES.authForgot, element: <Forgot /> },
          { path: ROUTES.authReset, element: <Reset /> },
          { path: ROUTES.authCheckEmail, element: <CheckEmail /> },
        ],
      },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <MarketingLayout />,
            children: [
              {
                element: <AccountLayout />,
                children: [
                  { path: ROUTES.account, element: <Account /> },
                  { path: ROUTES.accountStats, element: <AccountStats /> },
                  { path: ROUTES.accountDevices, element: <AccountDevices /> },
                  { path: ROUTES.accountPreferences, element: <AccountPreferences /> },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
